import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useHistory } from "react-router";
import ProspectOverviewSearchBar from "@icoach/nonMember/prospect/ProspectOverviewSearchBar";
import useOptions from "@util/hook/useOptions";
import useNonMemberApi from "@apis/useNonMemberApi";
import { OverviewListLayout, OverviewListToolbar, PageSortAndTotalBox, OverviewListActionBar } from "@icoach/components/overviewList/";
import { Button, Stack } from "@curves/ui-base";
import PageLayout from "@icoach/components/layout/PageLayout";
import OverflowMenu from "@icoach/components/overflowMenu/OverflowMenu";
import useOverview from "@util/hook/useOverview";
import { getProspectRowsLabel, menuNonMemberActions } from "@icoach/nonMember/nonMember.util";
import { SmsActionBar, AddInvalidActionBar, AddTemporaryActionBar, NonMemberDeleteActionBar } from "@icoach/nonMember/components/";
import usePrivacyToggle, { securityMaskFilterTypeMap } from "@util/hook/usePrivacyToggle";
import useCheckboxGroup from "@icoach/components/checkboxGroup/useCheckboxGroup";
import useTableScrollerTo from "@util/hook/useTableScrollerTo";
import { getDate, parseDate, getSubMonths } from "@util/moment";
import { isEmpty, isEqual } from "@util/utils";
import { CUSTOMER_VALID_TYPE, TARGET_TYPE, CUSTOMER_TYPE } from "@constants/";
import { NON_MEMBER_ROUTER } from "@icoach/router/routerPath";

/**
 * 預設搜尋參數
 * @type {Object}
 */
const defaultSearchParams = {
	pageIndex: 1,
	pageSize: 10,
	name: "", // 輸入框
	startDate: parseDate(getSubMonths(new Date(), 6)), // 詢問日期
	endDate: getDate(), // 詢問日期
	required: [], // 需求
	source: [], // 來源
	validType: CUSTOMER_VALID_TYPE.VALIDLIST, // 有效無效
};

const ProspectOverviewPage = ({ dialogRef: _dialogRef }) => {
	const overviewSearchBarRef = useRef(null);
	const history = useHistory();
	const tableRef = useRef(null);
	const { setCache, doScrollerTo, domKeyName } = useTableScrollerTo({ tableRef: tableRef, cacheKeyName: "nonmember-scroller", domKeyName: "data-customer-id", uniqueIdName: "customerID" });
	const { getInitProspectOptionsApi, postOverviewProspectApi } = useNonMemberApi();
	const { isRevealed, toggle, renderPrivacyIcon } = usePrivacyToggle();
	const { options } = useOptions({
		getOptionsApi: () => getInitProspectOptionsApi(),
		isFetchOnMount: true,
	});

	const { updateOverviewPage, searchParams, overviewData, getOverviewData } = useOverview({
		getOverviewApi: postOverviewProspectApi,
		defaultSearchParams: defaultSearchParams,
		enableInfiniteScroll: true,
	});
	const { getResult, Checkbox, CheckboxAll: CheckboxAllActionBar, onResetCheckBox, onUpdateCheckBox } = useCheckboxGroup({ tableRef });

	const { list, pagination } = overviewData || {};

	const listRef = useRef([]);
	const searchParamsRef = useRef(null);
	const nonMemberDescribeLabelBoxRef = useRef(null);

	// 簡訊是共用的所以來源要用 TargetType
	const handleCheckedData = () => {
		const target = getResult();
		if (target.length === 1) {
			const t = list.filter(({ customerID }) => isEqual(customerID, target[0]));
			return t.map(item => ({ id: item.customerID, name: item.name, type: TARGET_TYPE.CUSTOMER }));
		} else {
			return target.map(id => ({ id, type: TARGET_TYPE.CUSTOMER }));
		}
	};

	// 組暫存名單使用的搜尋條件
	const handleAddTemporaryData = () => {
		const { validType, source, required } = searchParams;
		const { validOptions, sourceOptions, requiredOptions } = options;
		let text = [];

		const sourceSplit = Array.isArray(source) ? source : [source];
		const requiredSplit = Array.isArray(required) ? required : [required];
		const ids = getResult();

		text.push(...validOptions.filter(({ value }) => isEqual(value, validType)).map(({ text }) => text));
		text.push(...sourceOptions.filter(({ value }) => sourceSplit.some(v => isEqual(v, value))).map(({ text }) => text));
		text.push(...requiredOptions.filter(({ value }) => requiredSplit.some(v => isEqual(v, value))).map(({ text }) => text));

		const params = {
			note: text.join("、"),
			list: ids,
			type: CUSTOMER_TYPE.GUEST,
		};

		return params;
	};

	// 新增無效名單資料 多選 / menu單選
	const handleAddInvalidData = target => {
		const ids = isEmpty(target) ? getResult() : target;
		return {
			list: ids,
			typeText: "名單",
		};
	};

	// 刪除顧客 多選 / menu單選
	const handleDeleteData = target => {
		const ids = isEmpty(target) ? getResult() : target;
		const info = listRef.current.filter(({ customerID }) => ids.some(id => isEqual(id, customerID))).map(({ name, cellPhone }) => ({ name, cellPhone }));
		return {
			list: ids,
			typeText: "名單",
			info: info,
		};
	};

	const handleAddInvalidRefresh = () => {
		handleSubmit();
	};

	const handleDeleteRefresh = () => {
		handleSubmit();
	};

	const handleContactRefresh = () => {
		handleSubmit();
	};

	const handleSubmit = () => {
		const params = overviewSearchBarRef.current.getResult();
		params.pageIndex = 1;
		params.pageSize = 10;
		doOverviewApi(params);
	};

	const handlePageChange = (e, page) => {
		doPageChangeApi(page);
	};

	// 預約下拉選單事件
	const handleAppointmentMenuClick = useCallback(
		cID => {
			const targetData = listRef.current;
			const q = searchParamsRef.current;
			setCache(targetData, cID, q);
			history.push(NON_MEMBER_ROUTER.CUSTOMER.GET_ADD_PATH(cID));
		},
		// eslint-disable-next-line
		[],
	);

	const doOverviewApi = async par => {
		await getOverviewData(par);
		onResetCheckBox();
	};

	const handleToggleRevealed = useCallback(
		() => {
			const apiParams = {
				filterType: securityMaskFilterTypeMap.ClubCustomerOverview,
				totalCount: listRef.current.length,
			};
			toggle(apiParams);
		},
		// eslint-disable-next-line
		[toggle],
	);

	const doPageChangeApi = async page => {
		await updateOverviewPage(page);
		onUpdateCheckBox();
	};

	const headerRow = useMemo(
		() => {
			const mAction = menuNonMemberActions({
				dialogRef: _dialogRef,
				onAddInvalidData: handleAddInvalidData,
				onDeleteData: handleDeleteData,
				onAddInvalidRefresh: handleAddInvalidRefresh,
				onDeleteRefresh: handleDeleteRefresh,
				onAppointmentClick: handleAppointmentMenuClick,
				onContactRefresh: handleContactRefresh,
			});
			const dropsMenu = [
				...getProspectRowsLabel(),
				{
					cellKey: "customerID",
					headerLabel: "",
					align: "center",
					formatCell: (cellValue, rowsData) => {
						return <OverflowMenu menuAction={mAction} sourceData={rowsData} nonMemberDescribeLabelBoxRef={nonMemberDescribeLabelBoxRef} />;
					},
				},
			];

			return dropsMenu;
		},
		// eslint-disable-next-line
		[],
	);

	// 盡量不要用，會不好理解和破壞render整體結構 by Allen
	useEffect(
		() => {
			listRef.current = list;
			if (list.length > 0) {
				doScrollerTo();
			}
		},
		// eslint-disable-next-line
		[list],
	);

	// 盡量不要用，會不好理解和破壞render整體結構 by Allen
	useEffect(
		() => {
			searchParamsRef.current = searchParams;
		},
		// eslint-disable-next-line
		[searchParams],
	);

	const { validType } = searchParams;

	return (
		<OverviewListLayout>
			<PageLayout.Header title="名單總覽">
				<Stack className={"ml-auto"} spacing={2}>
					<Button variant="contained" onClick={() => _dialogRef.current?.openProspectImportDialog(handleSubmit)}>
						名單上傳
					</Button>
				</Stack>
			</PageLayout.Header>
			<OverviewListLayout.SearchBar className={"mb-2"} onSubmit={handleSubmit}>
				<ProspectOverviewSearchBar key={options.length} ref={overviewSearchBarRef} options={options} sourceData={searchParams} />
			</OverviewListLayout.SearchBar>
			<OverviewListToolbar>
				{list.length !== 0 && (
					<OverviewListActionBar>
						<CheckboxAllActionBar />
						<SmsActionBar getResult={handleCheckedData} dialogRef={_dialogRef} />
						<AddTemporaryActionBar getResult={handleAddTemporaryData} dialogRef={_dialogRef} />
						{!isEqual(validType, CUSTOMER_VALID_TYPE.INVALIDLIST) && <AddInvalidActionBar getResult={handleAddInvalidData} dialogRef={_dialogRef} refresh={handleAddInvalidRefresh} />}
						<NonMemberDeleteActionBar getResult={handleDeleteData} dialogRef={_dialogRef} refresh={handleDeleteRefresh} />
						{renderPrivacyIcon(handleToggleRevealed, "has-divider")}
					</OverviewListActionBar>
				)}
				<PageSortAndTotalBox pageIndex={pagination.pageIndex} pageSize={pagination.pageSize} totalCount={pagination.totalCount} isCurrentPageLastIndex />
			</OverviewListToolbar>
			<OverviewListLayout.Table
				headerRow={headerRow}
				list={list}
				totalPage={overviewData?.pagination?.totalPage}
				pageIndex={overviewData?.pagination?.pageIndex}
				isMasonryPagination
				onChangePage={handlePageChange}
				tableBodyProps={{ ref: tableRef }}
				tableRowBodyProps={{ Checkbox: Checkbox, isRevealed: isRevealed, domKeyName: domKeyName, nonMemberDescribeLabelBoxRef: nonMemberDescribeLabelBoxRef }}
			/>
		</OverviewListLayout>
	);
};

export default ProspectOverviewPage;
